/**
 * Geometry of the navigation chart's nodes (chambers) and passages (connections): footprints for boxes, tubes and round
 * caves, distance queries, and polyline helpers. Pure maths, no DOM or Node APIs (runs in browser and Node).
 *
 * Coordinates are MAP space x / z plus the layered local Y used by the tile JSON (node.y + tile layer offset); depth is
 * -(baseDepth + y). Nodes passed in must already be shifted into that space (see navData.buildNavData).
 */
import type { AggregatedConnection, TileJsonNode } from './tileJson.js';

export type NodeShape = 'round' | 'box' | 'tube';
export type Vec3T = [number, number, number];

/** Tile JSON written before shapes existed (hand-made tiles) has none: it is a circle. */
export const nodeShapeOf = (node: Pick<TileJsonNode, 'shape'>): NodeShape => node.shape ?? 'round';

export const isBoxNode = (node: TileJsonNode): boolean =>
  nodeShapeOf(node) === 'box' && typeof node.half_x === 'number' && typeof node.half_z === 'number';

/** Half height of a node's volume (metres). */
export const nodeHalfY = (node: TileJsonNode): number => {
  if (nodeShapeOf(node) === 'box' && typeof node.half_y === 'number') return node.half_y;
  if (nodeShapeOf(node) === 'tube' && typeof node.half_y === 'number') return node.half_y;
  return node.radius_y;
};

const yawRadians = (node: TileJsonNode): number => ((node.yaw ?? 0) * Math.PI) / 180;

/** A point in the box's own axes (x along half_x, z along half_z), relative to the box centre. */
const toBoxLocal = (node: TileJsonNode, x: number, z: number): { lx: number; lz: number } => {
  const a = yawRadians(node);
  const c = Math.cos(a), s = Math.sin(a);
  const dx = x - node.x, dz = z - node.z;
  return { lx: dx * c + dz * s, lz: -dx * s + dz * c };
};

/** Plan outline of a box node as four map-space corners (clockwise in x/z), or null for round / tube nodes. */
export const nodePlanCorners = (node: TileJsonNode): Array<[number, number]> | null => {
  if (!isBoxNode(node)) return null;
  const hx = node.half_x!, hz = node.half_z!;
  const a = yawRadians(node);
  const c = Math.cos(a), s = Math.sin(a);
  return ([[-hx, -hz], [hx, -hz], [hx, hz], [-hx, hz]] as Array<[number, number]>).map(([lx, lz]) => [node.x + lx * c - lz * s, node.z + lx * s + lz * c]);
};

/** Distance in the plan (x / z) from a point to the node's footprint; 0 when inside. */
export const nodePlanDistance = (node: TileJsonNode, x: number, z: number): number => {
  if (isBoxNode(node)) {
    const { lx, lz } = toBoxLocal(node, x, z);
    return Math.hypot(Math.max(Math.abs(lx) - node.half_x!, 0), Math.max(Math.abs(lz) - node.half_z!, 0));
  }
  return Math.max(Math.hypot(x - node.x, z - node.z) - node.radius_xz, 0);
};

/** True when the plan point lies inside the footprint (optionally grown by `margin`). */
export const nodePlanContains = (node: TileJsonNode, x: number, z: number, margin = 0): boolean => nodePlanDistance(node, x, z) <= margin;

/**
 * Distance from a 3D point to the node's volume (0 inside). Boxes: exact; tubes: cylinder; round: distance along the ray to the
 * centre of the ellipsoid radius_xz x radius_y (exact on the axes, a close approximation elsewhere).
 */
export const nodeVolumeDistance = (node: TileJsonNode, x: number, y: number, z: number): number => {
  const dy = Math.abs(y - node.y);
  const shape = nodeShapeOf(node);
  if (shape === 'box' && isBoxNode(node)) {
    const { lx, lz } = toBoxLocal(node, x, z);
    return Math.hypot(Math.max(Math.abs(lx) - node.half_x!, 0), Math.max(dy - nodeHalfY(node), 0), Math.max(Math.abs(lz) - node.half_z!, 0));
  }
  const dxz = Math.hypot(x - node.x, z - node.z);
  if (shape === 'tube') return Math.hypot(Math.max(dxz - node.radius_xz, 0), Math.max(dy - nodeHalfY(node), 0));
  const ry = Math.max(node.radius_y, 1e-6), rxz = Math.max(node.radius_xz, 1e-6);
  const k = Math.hypot(dxz / rxz, dy / ry);
  if (k <= 1) return 0;
  return Math.hypot(dxz, dy) * (1 - 1 / k);
};

/**
 * Where the section line p(s) = origin + u*s passes through the node's footprint (plan). `frame` is a navSection SectionFrame (only
 * origin and u are used). Null when the line misses. Boxes are clipped exactly (rotated slabs), round / tube nodes as circles.
 */
export const nodeSectionSpan = (
  node: TileJsonNode,
  frame: { originX: number; originZ: number; ux: number; uz: number }
): { s0: number; s1: number } | null => {
  if (isBoxNode(node)) {
    const a = yawRadians(node);
    const c = Math.cos(a), s = Math.sin(a);
    const ox = frame.originX - node.x, oz = frame.originZ - node.z;
    const o: [number, number] = [ox * c + oz * s, -ox * s + oz * c];
    const u: [number, number] = [frame.ux * c + frame.uz * s, -frame.ux * s + frame.uz * c];
    const half = [node.half_x!, node.half_z!];
    let lo = -Infinity, hi = Infinity;
    for (let i = 0; i < 2; i += 1) {
      if (Math.abs(u[i]) < 1e-9) {
        if (Math.abs(o[i]) > half[i]) return null;
        continue;
      }
      let t0 = (-half[i] - o[i]) / u[i];
      let t1 = (half[i] - o[i]) / u[i];
      if (t0 > t1) { const t = t0; t0 = t1; t1 = t; }
      lo = Math.max(lo, t0);
      hi = Math.min(hi, t1);
      if (lo > hi) return null;
    }
    return hi > lo ? { s0: lo, s1: hi } : null;
  }
  const dx = frame.originX - node.x, dz = frame.originZ - node.z;
  const b = dx * frame.ux + dz * frame.uz;
  const cc = dx * dx + dz * dz - node.radius_xz * node.radius_xz;
  const disc = b * b - cc;
  if (disc <= 0) return null;
  const root = Math.sqrt(disc);
  return { s0: -b - root, s1: -b + root };
};

// ---- passages ---------------------------------------------------------------------------------------------------------------

/**
 * The centre line of a connection: from-node centre, its waypoints, to-node centre. Dead-end connections run from the node out to the
 * gateway (their last waypoint) and have no `to` node.
 */
export const connectionPolyline = (conn: AggregatedConnection, from: { x: number; y: number; z: number }, to: { x: number; y: number; z: number } | null): Vec3T[] => {
  const pts: Vec3T[] = [[from.x, from.y, from.z]];
  for (const w of conn.waypoints) pts.push([w[0], w[1], w[2]]);
  if (to && !conn.deadEnd) pts.push([to.x, to.y, to.z]);
  return pts;
};

export const polylineLength = (pts: ReadonlyArray<Vec3T>): number => {
  let total = 0;
  for (let i = 1; i < pts.length; i += 1) total += Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1], pts[i][2] - pts[i - 1][2]);
  return total;
};

/** Arc-length intervals of the polyline that lie inside the sphere (cx, cy, cz, r). Intervals are sorted and merged. */
export const polylineSphereSpans = (pts: ReadonlyArray<Vec3T>, cx: number, cy: number, cz: number, r: number): Array<[number, number]> => {
  const out: Array<[number, number]> = [];
  let base = 0;
  for (let i = 1; i < pts.length; i += 1) {
    const a = pts[i - 1], b = pts[i];
    const dx = b[0] - a[0], dy = b[1] - a[1], dz = b[2] - a[2];
    const len = Math.hypot(dx, dy, dz);
    if (len > 1e-9) {
      const fx = a[0] - cx, fy = a[1] - cy, fz = a[2] - cz;
      const qa = len * len;
      const qb = 2 * (fx * dx + fy * dy + fz * dz);
      const qc = fx * fx + fy * fy + fz * fz - r * r;
      const disc = qb * qb - 4 * qa * qc;
      if (disc >= 0) {
        const root = Math.sqrt(disc);
        const t0 = Math.max(0, (-qb - root) / (2 * qa));
        const t1 = Math.min(1, (-qb + root) / (2 * qa));
        if (t1 > t0) out.push([base + t0 * len, base + t1 * len]);
      }
    }
    base += len;
  }
  return mergeSpans(out);
};

/** Closest point of a polyline to a 3D point: its arc length and the distance. */
export const polylineClosest = (pts: ReadonlyArray<Vec3T>, x: number, y: number, z: number): { s: number; dist: number } => {
  let best = { s: 0, dist: Infinity };
  let base = 0;
  if (pts.length === 1) return { s: 0, dist: Math.hypot(x - pts[0][0], y - pts[0][1], z - pts[0][2]) };
  for (let i = 1; i < pts.length; i += 1) {
    const a = pts[i - 1], b = pts[i];
    const dx = b[0] - a[0], dy = b[1] - a[1], dz = b[2] - a[2];
    const len2 = dx * dx + dy * dy + dz * dz;
    const len = Math.sqrt(len2);
    const t = len2 > 1e-12 ? Math.min(1, Math.max(0, ((x - a[0]) * dx + (y - a[1]) * dy + (z - a[2]) * dz) / len2)) : 0;
    const d = Math.hypot(x - (a[0] + dx * t), y - (a[1] + dy * t), z - (a[2] + dz * t));
    if (d < best.dist) best = { s: base + t * len, dist: d };
    base += len;
  }
  return best;
};

export const mergeSpans = (spans: Array<[number, number]>): Array<[number, number]> => {
  const sorted = spans.filter((s) => s[1] > s[0]).sort((a, b) => a[0] - b[0]);
  const out: Array<[number, number]> = [];
  for (const s of sorted) {
    const last = out[out.length - 1];
    if (last && s[0] <= last[1] + 1e-6) last[1] = Math.max(last[1], s[1]);
    else out.push([s[0], s[1]]);
  }
  return out;
};

/** The pieces of a polyline inside the given arc-length spans, as separate polylines (so gaps stay gaps). */
export const clipPolylineToSpans = (pts: ReadonlyArray<Vec3T>, spans: ReadonlyArray<[number, number]>): Vec3T[][] => {
  const result: Vec3T[][] = [];
  if (pts.length < 2) return result;
  const cum: number[] = [0];
  for (let i = 1; i < pts.length; i += 1) cum.push(cum[i - 1] + Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1], pts[i][2] - pts[i - 1][2]));
  const at = (s: number): Vec3T => {
    for (let i = 1; i < pts.length; i += 1) {
      if (s <= cum[i] || i === pts.length - 1) {
        const seg = cum[i] - cum[i - 1];
        const t = seg > 1e-9 ? Math.min(1, Math.max(0, (s - cum[i - 1]) / seg)) : 0;
        return [pts[i - 1][0] + (pts[i][0] - pts[i - 1][0]) * t, pts[i - 1][1] + (pts[i][1] - pts[i - 1][1]) * t, pts[i - 1][2] + (pts[i][2] - pts[i - 1][2]) * t];
      }
    }
    return [pts[0][0], pts[0][1], pts[0][2]];
  };
  for (const [s0, s1] of mergeSpans(spans.map((s) => [s[0], s[1]] as [number, number]))) {
    const piece: Vec3T[] = [at(s0)];
    for (let i = 1; i < pts.length - 1; i += 1) if (cum[i] > s0 && cum[i] < s1) piece.push([pts[i][0], pts[i][1], pts[i][2]]);
    piece.push(at(s1));
    result.push(piece);
  }
  return result;
};
